/**
 * Flatten every date-like value in a payload bound for the backend to an ISO
 * string.
 *
 * A record that has round-tripped through the Desktop replica carries its dates
 * as `{seconds, nanoseconds}` maps rather than real timestamps: the replication
 * batch serialises a Firestore `Timestamp` with `JSON.parse(JSON.stringify(…))`
 * and recovery writes that plain object straight back into Firestore. The
 * backend's model binder rejects the map with `Unexpected character encountered
 * while parsing value.
 *
 * Only date-shaped values are rewritten — a `{seconds, nanoseconds}` pair, a
 * Firestore `Timestamp`, or a `Date`. Everything else is returned untouched, so
 * strings that merely look numeric (SKUs, part numbers, closure codes) keep
 * their type. The input is never mutated; the Firestore cache hands out live
 * objects and the wire shape must not write back into them.
 */
export function normalizeWireDates<T>(value: T): T {
  return normalize(value) as T;
}

function normalize(value: any): any {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return value;

  const date = toDate(value);
  if (date) return date.toISOString();

  if (Array.isArray(value)) return value.map(normalize);

  const result: Record<string, any> = {};
  for (const key of Object.keys(value)) {
    result[key] = normalize(value[key]);
  }
  return result;
}

/** A `Date` when `value` is a recognisable timestamp, otherwise `undefined`. */
function toDate(value: any): Date | undefined {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? undefined : value;
  }

  // Firestore Timestamp (or anything else exposing the same accessor).
  if (typeof value.toDate === 'function') {
    const converted = value.toDate();
    return converted instanceof Date && !isNaN(converted.getTime()) ? converted : undefined;
  }

  // A serialised Timestamp. Both halves are required so an unrelated object
  // that happens to carry a `seconds` field is not mistaken for a date.
  const seconds = timestampPart(value, 'seconds');
  const nanoseconds = timestampPart(value, 'nanoseconds');
  if (seconds === undefined || nanoseconds === undefined) return undefined;

  const converted = new Date(seconds * 1000 + Math.floor(nanoseconds / 1e6));
  return isNaN(converted.getTime()) ? undefined : converted;
}

/** Read one half of a serialised Timestamp; the REST shape quotes both. */
function timestampPart(value: any, key: 'seconds' | 'nanoseconds'): number | undefined {
  const raw = value[key] ?? value[`_${key}`];
  if (raw === null || raw === undefined) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}
